export type RoundEndReason = "timer";

export interface RoundState {
  readonly result: RoundEndReason | null;
  readonly timeRemainingMs: number | null;
}

export interface RoundEndTransition {
  readonly accepted: boolean;
  readonly state: RoundState;
}

const validateDuration = (durationMs: number | null): void => {
  if (
    durationMs !== null &&
    (!Number.isSafeInteger(durationMs) || durationMs <= 0)
  ) {
    throw new RangeError("Round duration must be a positive safe integer");
  }
};

const validateAdvance = (milliseconds: number): void => {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) {
    throw new RangeError("Round advance must be non-negative and finite");
  }
};

const isRoundEndReason = (reason: string): reason is RoundEndReason =>
  reason === "timer";

export const createRoundState = (durationMs: number | null): RoundState => {
  validateDuration(durationMs);
  return { result: null, timeRemainingMs: durationMs };
};

export const advanceRound = (
  state: RoundState,
  milliseconds: number,
): RoundState => {
  validateAdvance(milliseconds);
  if (
    milliseconds === 0 ||
    state.result !== null ||
    state.timeRemainingMs === null
  ) {
    return state;
  }
  const timeRemainingMs = Math.max(0, state.timeRemainingMs - milliseconds);
  return {
    result: timeRemainingMs === 0 ? "timer" : null,
    timeRemainingMs,
  };
};

export const requestRoundEnd = (
  state: RoundState,
  reason: RoundEndReason,
): RoundEndTransition => {
  if (
    state.timeRemainingMs === null ||
    state.result !== null ||
    !isRoundEndReason(reason)
  ) {
    return { accepted: false, state };
  }
  return {
    accepted: true,
    state: {
      result: reason,
      timeRemainingMs: 0,
    },
  };
};
